import { X } from "lucide-react";
import { useId } from "react";
import { createPortal } from "react-dom";

type MetricScope = "content" | "account" | "summary";

function metricDefinition(label: string, scope: MetricScope, rateBasis: "engagement" | "audienceViews") {
  switch (label.toLowerCase().replace(/^social /, "")) {
    case "reach":
      return {
        summary: "Unique accounts that saw the content at least once, within the source's reporting period.",
        detail: scope === "content"
          ? "Instagram Reach counts accounts that saw a post. Repeat views by the same account do not increase reach. Facebook uses Unique Viewers instead."
          : "The dashboard uses account reach when available, with content reach as a fallback. Totals across posts or accounts can count the same person more than once."
      };
    case "unique viewers":
    case "reach / unique viewers":
      return {
        summary: "Facebook uses Unique Viewers; Instagram uses Reach. Both differ from repeat views.",
        detail: scope === "content"
          ? "Facebook uses the latest lifetime post_total_media_view_unique observation. Instagram uses post reach. These platform metrics have different definitions and should not be treated as identical audiences."
          : "Facebook uses page unique viewers when available, with the sum of latest post unique viewers as a fallback. Post and account totals can count the same person more than once; they are not a deduplicated combined audience. Instagram retains Reach."
      };
    case "views":
      return {
        summary: "The number of times content was displayed or played, rather than the number of unique accounts.",
        detail: scope === "content"
          ? "Instagram uses the reported views metric. Facebook uses post_media_view, with video views as a fallback for video posts. Repeat-view rules depend on the source metric."
          : "The dashboard combines reported views and video views, with content views as a fallback when account views are unavailable. These source metrics may overlap and are not a deduplicated audience count."
      };
    case "engagements":
      return {
        summary: "Interaction actions, not unique people. One person can contribute more than one action.",
        detail: scope === "content"
          ? "Facebook: reactions + comments + shares. Instagram: reported total interactions, or likes + comments + shares + saves when that total is unavailable. Separately collected clicks are not added to this post total."
          : "Account and content engagement signals are added together. Content signals include reactions or likes, comments, shares and Instagram saves. These totals can overlap; they are not unique engaged people or a deduplicated interaction count."
      };
    case "rate":
      if (rateBasis === "audienceViews") return {
        summary: "Facebook: Unique Viewers / Views x 100. Instagram: Reach / Views x 100.",
        detail: "This is an audience-to-views ratio, not engagement rate. Shown as n/a when audience data is missing or Views is missing or zero. Platform metric definitions differ; values are not capped at 100%. Top Social Content is ordered by audience descending, then Views descending."
      };
      return {
        summary: "Facebook: Engagements / Unique Viewers x 100. Instagram: Engagements / Reach x 100.",
        detail: "This is an interaction-to-audience ratio, not the percentage of unique people who interacted. It is shown as n/a when the denominator is missing or zero. Different platform denominators are not directly comparable."
      };
    default:
      return undefined;
  }
}

export function MarketingMetricHelp({ label, scope = "summary", rateBasis = "engagement" }: { label: string; scope?: MetricScope; rateBasis?: "engagement" | "audienceViews" }) {
  const id = useId();
  const definition = metricDefinition(label, scope, rateBasis);
  if (!definition) return <>{label}</>;

  return (
    <>
      <button
        className="marketing-metric-help"
        type="button"
        popoverTarget={id}
        aria-label={`${label}: metric definition`}
        title={definition.summary}
      >
        {label}
      </button>
      {createPortal(
        <div className="marketing-metric-definition" id={id} popover="auto" role="dialog" aria-labelledby={`${id}-title`}>
          <div className="marketing-metric-definition__heading">
            <h3 id={`${id}-title`}>{label}</h3>
            <button type="button" popoverTarget={id} popoverTargetAction="hide" aria-label="Close metric definition" title="Close">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <p>{definition.summary}</p>
          <p>{definition.detail}</p>
          <p className="marketing-metric-definition__note">
            Missing data is not zero activity. Post reach and views show n/a when unavailable; some account and summary totals currently substitute 0 for missing values. Post metrics reflect the latest collected snapshot, not necessarily activity during the selected dates.
          </p>
        </div>,
        document.body
      )}
    </>
  );
}
